import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { findJobQuantityByAssignment } from '../../api/jobQuantityApi.js';
import { JobQuantityPanel } from '../billing/JobQuantityPanel.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { quantityLine } from './JobAssignmentSummary.js';
import {
  ASSIGNMENT_ACCEPTED,
  ASSIGNMENT_CLARIFICATION,
  ASSIGNMENT_PENDING_ACCEPTANCE,
} from '../../../../shared/contracts/backend-enums.js';

/** Mirrors backend T090VendorAssignment (GET /v1/vendor-assignments/:id, §8). */
interface JobContext {
  readonly jobNumber: string | null;
  readonly customerName: string | null;
  readonly dealerCode: string | null;
  readonly dealerName: string | null;
  readonly siteAddress: string | null;
  readonly city: string | null;
  readonly pinCode: string | null;
  readonly contactPerson: string | null;
  readonly contactMobile: string | null;
  readonly district: string | null;
  readonly state: string | null;
  readonly areaRequested: number | null;
  readonly areaUnit: string | null;
  readonly currentStatus: string | null;
  readonly remarks: string | null;
}

interface AssignmentDetail {
  readonly assignmentId: string;
  readonly jobRequestId: string;
  readonly vendorId: string;
  readonly assignedAt: string;
  readonly expectedStartDate: string | null;
  readonly expectedCompletionDate: string | null;
  readonly priority: string;
  readonly specialInstructions: string | null;
  readonly remarks: string | null;
  readonly acceptanceDueAt: string;
  readonly targetSlaMinutes: number | null;
  readonly attachments: ReadonlyArray<{ readonly id: string; readonly filename?: string }>;
  readonly status: string;
  readonly assignmentCode: string | null;
  readonly woNumber: string | null;
  readonly allocatedQuantity: number | null;
  readonly unitOfMeasure: string | null;
  readonly job: JobContext | null;
}

/** The dealer's shop, written the way an address is read. */
function siteLine(job: JobContext | null): string {
  if (!job) return '—';
  const parts = [job.siteAddress, job.city, job.district, job.state, job.pinCode];
  return parts.filter((p) => p && String(p).trim().length > 0).join(', ') || '—';
}

function dealerLine(job: JobContext | null): string {
  if (!job) return '—';
  const name = job.dealerName ?? job.customerName;
  if (name && job.dealerCode) return `${name} · ${job.dealerCode}`;
  return name ?? job.dealerCode ?? '—';
}

const PENDING = ASSIGNMENT_PENDING_ACCEPTANCE;
const ACCEPTED = ASSIGNMENT_ACCEPTED;

export function VendorJobDetailPage(): ReactNode {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const { day, at } = useBusinessDay();
  const { decimal } = useBusinessNumber();
  const [job, setJob] = useState<AssignmentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The job's quantity, read by the assignment it is recorded against.
  const fetchQuantity = useCallback(
    () => findJobQuantityByAssignment(token ?? '', assignmentId ?? ''),
    [token, assignmentId],
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiRequest<AssignmentDetail>(`/v1/vendor-assignments/${assignmentId}`, { token });
        if (alive) setJob(r);
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [assignmentId, token]);

  if (error) return <main aria-label="vendor-job-detail"><p role="alert">{error}</p></main>;
  if (!job) return <main aria-label="vendor-job-detail"><p>{t('common.loading')}</p></main>;

  const pending = job.status === PENDING;
  const canClarify = job.status === PENDING || job.status === ASSIGNMENT_CLARIFICATION;
  // Painter allocation is refused by the backend until the vendor has accepted
  // (PainterAssignmentService.assign → VENDOR_HAS_NOT_ACCEPTED). Offering it
  // earlier walks the vendor through picking a painter, dates and priority only
  // to be turned away on submit.
  const canAssignPainter = job.status === ACCEPTED;

  return (
    <main aria-label="vendor-job-detail">
      <h1>{t('vops.jobDetail.title', { code: job.job?.jobNumber ?? job.assignmentCode ?? '—' })}</h1>
      <p>
        {t('vops.common.status')} <strong aria-label="job-status">{job.status}</strong> · {t('vops.common.priority')}{' '}
        <strong aria-label="job-priority">{job.priority}</strong>
      </p>

      <dl aria-label="job-fields">
        <dt>{t('vops.painter.ctxDealer')}</dt><dd aria-label="field-dealer">{dealerLine(job.job)}</dd>
        <dt>{t('vops.painter.ctxSite')}</dt><dd aria-label="field-site">{siteLine(job.job)}</dd>
        <dt>{t('vops.painter.ctxContact')}</dt>
        <dd aria-label="field-contact">{[job.job?.contactPerson, job.job?.contactMobile].filter(Boolean).join(' · ') || '—'}</dd>
        <dt>{t('vops.painter.ctxWorkOrder')}</dt>
        <dd aria-label="field-work-order">{job.woNumber ?? '—'}</dd>
        <dt>{t('vops.jobDetail.assignmentCode')}</dt>
        <dd aria-label="field-assignment-code">{job.assignmentCode ?? '—'}</dd>
        <dt>{t('vops.painter.ctxQuantity')}</dt>
        <dd aria-label="field-quantity">{quantityLine(job, decimal)}</dd>
        <dt>{t('vops.jobDetail.assigned')}</dt><dd aria-label="field-assignedAt">{at(job.assignedAt)}</dd>
        <dt>{t('vops.jobDetail.acceptanceDue')}</dt><dd aria-label="field-acceptanceDueAt">{at(job.acceptanceDueAt)}</dd>
        <dt>{t('vops.jobDetail.expectedStart')}</dt><dd aria-label="field-expectedStartDate">{day(job.expectedStartDate)}</dd>
        <dt>{t('vops.jobDetail.expectedCompletion')}</dt><dd aria-label="field-expectedCompletionDate">{day(job.expectedCompletionDate)}</dd>
        <dt>{t('vops.jobDetail.targetSla')}</dt><dd aria-label="field-targetSlaMinutes">{job.targetSlaMinutes ?? '—'}</dd>
        <dt>{t('vops.jobDetail.specialInstructions')}</dt><dd aria-label="field-specialInstructions">{job.specialInstructions ?? '—'}</dd>
        <dt>{t('vops.jobDetail.remarks')}</dt><dd aria-label="field-remarks">{job.remarks ?? '—'}</dd>
      </dl>

      <section aria-label="attachments">
        <h2>{t('vops.jobDetail.attachments')}</h2>
        {job.attachments.length === 0 ? (
          <p aria-label="attachments-empty">{t('vops.jobDetail.noAttachments')}</p>
        ) : (
          <ul>
            {job.attachments.map((a) => (
              <li key={a.id} aria-label={`attachment-${a.id}`}>{a.filename ?? a.id}</li>
            ))}
          </ul>
        )}
      </section>

      <nav aria-label="job-actions" className="tl-toolbar">
        {pending ? (
          <>
            <Link className="tl-btn tl-btn--primary" to={`/vendor-ops/jobs/${job.assignmentId}/accept`} aria-label="action-accept">{t('vops.jobDetail.actionAccept')}</Link>
            <Link className="tl-btn tl-btn--danger" to={`/vendor-ops/jobs/${job.assignmentId}/reject`} aria-label="action-reject">{t('vops.jobDetail.actionReject')}</Link>
          </>
        ) : null}
        {canClarify ? (
          <Link className="tl-btn tl-btn--secondary" to={`/vendor-ops/jobs/${job.assignmentId}/clarify`} aria-label="action-clarify">{t('vops.jobDetail.actionClarify')}</Link>
        ) : null}
        {canAssignPainter ? (
          <Link className="tl-btn tl-btn--primary" to={`/vendor-ops/jobs/${job.assignmentId}/painter`} aria-label="action-painter">{t('vops.jobDetail.actionPainter')}</Link>
        ) : null}
        <Link className="tl-btn tl-btn--secondary" to={`/vendor-ops/jobs/${job.assignmentId}/execution`} aria-label="action-execution">{t('vops.jobDetail.actionExecution')}</Link>
        <Link className="tl-btn tl-btn--secondary" to={`/vendor-ops/jobs/${job.assignmentId}/documents`} aria-label="action-documents">{t('vops.jobDetail.actionDocuments')}</Link>
        <Link className="tl-btn tl-btn--secondary" to={`/vendor-ops/jobs/${job.assignmentId}/communication`} aria-label="action-communication">{t('vops.jobDetail.actionCommunication')}</Link>
      </nav>

      {/* The job's quantity: stated, corrected and billed on this one panel,
          the same one every reviewer works on the review screen. */}
      <section aria-label="job-quantity-section">
        <h2>{t('quality.area.quantity')}</h2>
        <JobQuantityPanel
          key={job.assignmentId}
          fetchRow={fetchQuantity}
          notOpened={t('quality.area.quantityNotOpened')}
          assignmentId={job.assignmentId}
        />
      </section>
    </main>
  );
}
